import { useState } from 'react';
import { Tag, Copy, CheckCircle, Sparkles } from 'lucide-react';
import { cn } from '@/utils/cn';

interface CouponBannerProps {
  code: string;
  description: string;
  variant?: 'banner' | 'card' | 'inline';
  showCopy?: boolean;
  className?: string;
}

const CouponBanner = ({ 
  code, 
  description, 
  variant = 'banner',
  showCopy = true,
  className 
}: CouponBannerProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getBannerStyles = () => {
    switch (variant) {
      case 'banner':
        return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-xl shadow-lg';
      case 'card':
        return 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800 py-2 px-3 rounded-lg';
      case 'inline':
        return 'bg-green-100 text-green-800 py-1 px-2 rounded-md text-sm';
      default:
        return 'bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3 px-4 rounded-xl shadow-lg';
    }
  };

  const getCodeStyles = () => {
    switch (variant) {
      case 'banner':
        return 'bg-white/20 text-white px-3 py-1 rounded-lg font-mono font-bold text-lg';
      case 'card':
        return 'bg-green-200/50 text-green-900 px-2 py-1 rounded font-mono font-bold';
      case 'inline':
        return 'bg-green-200 text-green-900 px-1 py-0.5 rounded font-mono font-semibold text-xs';
      default:
        return 'bg-white/20 text-white px-3 py-1 rounded-lg font-mono font-bold text-lg';
    }
  };

  const getIconSize = () => {
    switch (variant) {
      case 'banner':
        return 'h-5 w-5';
      case 'card':
        return 'h-4 w-4';
      case 'inline':
        return 'h-3 w-3';
      default:
        return 'h-5 w-5';
    }
  };

  if (variant === 'inline') {
    return (
      <div className={cn('inline-flex items-center gap-1', getBannerStyles(), className)}>
        <Tag className={getIconSize()} />
        <span className={getCodeStyles()}>{code}</span>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex items-center gap-2 sm:gap-4 animate-pulse-glow',
      getBannerStyles(),
      className
    )}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Sparkles className={cn(getIconSize(), 'animate-spin-slow')} />
          <Tag className={getIconSize()} />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
          <span className={cn(
            'font-semibold truncate',
            variant === 'banner' ? 'text-sm sm:text-base' : 'text-sm'
          )}>
            {description}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={cn(
              'text-xs whitespace-nowrap',
              variant === 'banner' ? 'text-white/80' : 'text-green-700'
            )}>
              Use code:
            </span>
            <span className={cn(getCodeStyles(), 'whitespace-nowrap')}>{code}</span>
          </div>
        </div>
      </div>
      
      {showCopy && (
        <button
          onClick={handleCopy}
          className={cn(
            'flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg transition-all duration-200 font-medium text-xs sm:text-sm flex-shrink-0',
            variant === 'banner' 
              ? 'bg-white/20 hover:bg-white/30 text-white' 
              : 'bg-green-200 hover:bg-green-300 text-green-800',
            'hover:scale-105 active:scale-95'
          )}
        >
          {copied ? (
            <>
              <CheckCircle className="h-4 w-4" aria-label="CheckCircle icon" />
              <span className="hidden sm:inline">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" aria-label="Copy icon" />
              <span className="hidden sm:inline">Copy</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CouponBanner;